import { DateTime } from "luxon";
import { GET_TEAMS } from "../graphql/teamGraphql";

export const DATETIME_FORMAT = "yyyy/MM/dd HH:mm";
export const DATE_FORMAT = "yyyy/MM/dd";
export const TIME_FORMAT = "HH:mm";

export const emailRegex =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const capitalizeFirstLetter = (str = "", locale = "en") =>
  str ? str[0].toLocaleUpperCase(locale) + str.slice(1) : "";

export const toTitleCase = (str = "", locale = "en") =>
  str
    .toLocaleLowerCase(locale)
    .split(" ")
    .map(([first, ...rest]) =>
      first ? first.toLocaleUpperCase(locale) + rest.join("") : ""
    )
    .join(" ");

export const getSeasonYear = () => {
  const currentDate = DateTime.now();

  // If we are before July (month 7), season started last year
  if (currentDate.month < 7) {
    return currentDate.year - 1;
  }
  return currentDate.year;
};

export const getSeasonDate = (format = DATETIME_FORMAT) => {
  // start date is 1st of July
  return DateTime.fromObject({
    year: getSeasonYear(),
    month: 7,
    day: 1,
  })
    .startOf("day")
    .toFormat(format);
};

export const getLastDayOfSeason = (format = DATETIME_FORMAT) => {
  // end date is 30th of June
  return DateTime.fromObject({
    year: getSeasonYear() + 1,
    month: 6,
    day: 30,
  })
    .endOf("day")
    .toFormat(format);
};

export const getSeasons = () => {
  const baseYear = getSeasonYear(); // season start year, not just calendar year
  const seasons = [];
  for (let i = baseYear - 2; i <= baseYear + 2; i++) {
    if (i < 0) continue;
    seasons.push({
      text: `${i}/${String(i + 1).slice(-2)}`, // e.g. "2025/26"
      value: `${i}`,
    });
  }
  return seasons;
};

export function formattedDate(date, format = DATETIME_FORMAT) {
  if (!date) {
    return null;
  }

  return DateTime.fromFormat(date, format);
}

export const getOldDate = (event, withoutTime = false) => {
  return formattedDate(event?.fromDate)?.toFormat(
    `EEEE d MMMM${withoutTime ? "" : ", HH:mm"}`,
    { locale: "el" }
  );
};

export const formatSeason = (startYear) => {
  if (!startYear) return "";
  return `${startYear}/${String(Number(startYear) + 1).slice(-2)}`;
};

export const getMimeType = (fileExtension) => {
  const mimeTypes = {
    svg: "image/svg+xml",
    png: "image/png",
    jpeg: "image/jpeg",
    jpg: "image/jpeg",
    gif: "image/gif",
    webp: "image/webp",
    mp4: "video/mp4",
    mpeg: "video/mpeg",
    avi: "video/x-msvideo",
    pdf: "application/pdf",
  };

  return mimeTypes[fileExtension.toLowerCase()];
};

export const createEmptyData = () => {
  const array = [];
  for (let i = 0; i <= 3; i++) {
    array.push({ skeleton: true });
  }
  return array;
};

export const getAgeGroup = (ageGroup) => {
  if (!isNaN(ageGroup)) {
    return `U${ageGroup}`;
  } else {
    return ageGroup.charAt(0).toUpperCase() + ageGroup.slice(1);
  }
};

export const getProfilesTeamDetails = async (
  clubId,
  axiosGraphQL,
  className,
  snackBar,
  errorLog
) => {
  try {
    let teamDetailsResponse = await axiosGraphQL({
      query: GET_TEAMS,
      variables: { clubId, skip: 0, limit: 10 },
    });

    return teamDetailsResponse.data.data.getTeamsAdmin.reduce((total, team) => {
      total[team?._id] = team;
      return total;
    }, {});
  } catch (error) {
    errorLog(error, className, "getProfilesTeamDetails", "GET_TEAMS");
    snackBar.setOptions({
      alertMessage: error.message,
      alertSeverity: "error",
    });
    snackBar.setSnackbarOpen(true);
  }

  return null;
};

export const generateLeagueCompetitions = (competitions) => {
  const fixtures = flattenFixtures(competitions);

  return mapCompetitions(competitions, fixtures);
};

const flattenFixtures = (competitions) => {
  const fixtures = [];
  competitions.forEach((competition) => {
    const competitionFixtures = competition.fixtures.map((fixture) => {
      const fixtureMatches = fixture.matches.map((match) => {
        return {
          homeTeam: match.homeTeam,
          awayTeam: match.awayTeam,
          date: match.date ? match.date.toFormat("y/LL/dd") : "TBD",
          time: match.time ? match.time.toFormat("HH:mm") : "TBD",
          duration: match.duration,
          timezone: match.timezone,
          status: match.status,
          score: match.score,
          venue: match.venue,
          competitionId: match.competitionId,
          fixtureId: match.fixtureId,
          leagueId: match.leagueId,
        };
      });

      return {
        fixtureName: fixture.fixtureName,
        leagueId: fixture.leagueId,
        competitionId: fixture.competitionId,
        matches: fixtureMatches,
      };
    });

    fixtures.push(...competitionFixtures);
  });

  return fixtures;
};

const flattenLocation = (location) => {
  return {
    type: "Point",
    address: location?.address,
    coordinates: !(location?.lng && location?.lat)
      ? []
      : [location?.lng, location?.lat],
  };
};

const mapCompetitions = (competitions, fixtures) => {
  return competitions.map((competition, _index) => {
    const competitionFixtures = fixtures.filter(
      (fixture) => fixture.competitionId === competition._id
    );

    return {
      type: competition.type,
      name: competition.name,
      ageGroup: competition.ageGroup,
      gender: competition.gender,
      startYear: competition.startYear,
      teams: competition.teams,
      location: flattenLocation(competition.location),
      fixtures: competitionFixtures,
    };
  });
};
